import React, { useEffect, useRef, useState } from "react";

interface UnitFormProps {
  handleSubmit: (e: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formData: any;
  errors: any;
  isSubmitting: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  videoRef: React.RefObject<HTMLInputElement>;
  zipRef: React.RefObject<HTMLInputElement>;
  cohortOptions: any[];
  cohortLoading: boolean;
  onSelectCourse: (course: any) => void;
  onOpenCourseDropdown: () => void;
}

// Works whether the field currently holds a File object (just picked,
// not uploaded yet) or a string URL (already uploaded / from the server).
const getFileName = (value: unknown): string => {
  if (!value) return "";
  if (value instanceof File) return value.name;
  if (typeof value === "string") return value.split("/").pop() || value;
  return "";
};

const isUrlString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const UnitForm: React.FC<UnitFormProps> = ({
  fileRef,
  videoRef,
  zipRef,
  formData,
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  cohortOptions,
  cohortLoading,
  onSelectCourse,
  onOpenCourseDropdown,
}) => {
  const [topicOpen, setTopicOpen] = useState(false);
  const topicRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (topicRef.current && !topicRef.current.contains(e.target as Node)) {
        setTopicOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // FIX: selecting a course used to be entirely local (setcohort +
  // localStorage) and never touched formData, so it never reached the
  // add/update payload. The dropdown's fetch was also duplicated here
  // (its own axios call) instead of reusing the parent's cohortOptions.
  // Both are now driven by props from the parent, and the displayed
  // value comes from formData.subject so it clears automatically when
  // formData resets after a submit.

  return (
    <div>
      <style>
        {`
        .topic-select-wrap {
  position: relative;
  margin-top: 4px;
}

.topic-select-trigger {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #f8f9fa;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  padding: 0 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.topic-select-trigger:hover {
  background: #eef1f0;
}

.topic-select-trigger.open {
  border-color: #16a34a;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.15);
}

.topic-select-trigger.has-error {
  border-color: #dc3545;
}

.topic-select-value {
  flex: 1;
  min-width: 0;
  text-align: left;
  font-size: 14px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.topic-select-value.placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.topic-select-caret {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6b7280;
  font-size: 13px;
  transition: transform 0.25s ease, color 0.2s ease;
}

.topic-select-trigger.open .topic-select-caret {
  transform: rotate(180deg);
  color: #16a34a;
}

.topic-select-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  list-style: none;
  margin: 0;
  padding: 6px;
  max-height: 220px;
  overflow-y: auto;
  z-index: 20;
  animation: topicFadeIn 0.15s ease;
}

@keyframes topicFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.topic-select-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 10px;
  border-radius: 6px;
  font-size: 13.5px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.topic-select-option:hover {
  background: #f0fdf4;
  color: #16a34a;
}

.topic-select-option.selected {
  background: #f0fdf4;
  color: #16a34a;
  font-weight: 600;
}

.topic-select-option-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.topic-select-empty {
  padding: 10px;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}`}
      </style>
      <form onSubmit={handleSubmit} className="py-2">
        <div className="row p-0 px-2">
          {/* Select Course */}
          <div className="col-md-6 mb-3">
            <label>
              <strong>
                Select Course<sup className="text-danger">*</sup>
              </strong>
            </label>

            <div className="topic-select-wrap" ref={topicRef}>
              <button
                type="button"
                className={`topic-select-trigger ${errors?.course_id ? "has-error" : ""} ${topicOpen ? "open" : ""}`}
                onClick={() => {
                  onOpenCourseDropdown();
                  setTopicOpen((prev) => !prev);
                }}
                aria-haspopup="listbox"
                aria-expanded={topicOpen}
              >
                <span className={`topic-select-value`}>
                  {formData?.subject || "Select Course"}
                </span>
                <span className="topic-select-caret">
                  <i className="ti ti-chevron-down" />
                </span>
              </button>

              {topicOpen && (
                <ul className="topic-select-menu" role="listbox">
                  {cohortLoading ? (
                    <li className="topic-select-empty">Loading...</li>
                  ) : cohortOptions?.length === 0 ? (
                    <li className="topic-select-empty">No topics found</li>
                  ) : (
                    cohortOptions?.map((t: any) => (
                      <li
                        key={t?.acdmc_course_id}
                        role="option"
                        aria-selected={formData?.subject === t?.course_name}
                        className={`topic-select-option ${formData?.subject === t.course_name ? "selected" : ""}`}
                        onClick={() => {
                          onSelectCourse(t);
                          setTopicOpen(false);
                        }}
                      >
                        <span className="topic-select-option-text">
                          {t?.course_name}
                        </span>
                        {formData?.subject === t.course_name && (
                          <i className="ti ti-check" />
                        )}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>

            {errors?.course_id && (
              <p className="p-0 ps-1 pt-1 text-danger">{errors.course_id}</p>
            )}
          </div>

          <div className="col-md-6 mb-3">
            <label>
              <strong>
                Lesson Topic<sup className="text-danger">*</sup>
              </strong>
            </label>

            <input
              type="text"
              name="topic"
              value={formData.topic || ""}
              onChange={handleChange}
              className="form-control mt-1"
              placeholder="Enter lesson topic"
            />

            {errors?.topic && (
              <p className="p-0 ps-1 pt-1 text-danger">{errors.topic}</p>
            )}
          </div>

          {/* Lesson Unit */}
          <div className="col-md-6 mb-3">
            <label>
              <strong>
                Lesson Unit<sup className="text-danger">*</sup>
              </strong>
            </label>

            <input
              type="text"
              name="unit"
              value={formData.unit || ""}
              onChange={handleChange}
              className="form-control mt-1"
              placeholder="Enter lesson unit"
            />

            {errors?.unit && (
              <p className="p-0 ps-1 pt-1 text-danger">{errors.unit}</p>
            )}
          </div>

          {/* Training Presentation */}
          <div className="col-md-6 mb-3">
            <label>
              <strong>
                Training Presentation
                <sup className="text-danger">*</sup>
              </strong>
            </label>

            <input
              type="file"
              accept=".pdf,application/pdf"
              ref={fileRef}
              name="lesson_pdf_path"
              multiple
              onChange={handleChange}
              className="form-control mt-1"
            />

            {errors?.lesson_pdf_path && (
              <p className="p-0 ps-1 pt-1 text-danger">
                {errors.lesson_pdf_path}
              </p>
            )}

            {formData?.lesson_pdf_path &&
              Array.from(
                Array.isArray(formData.lesson_pdf_path)
                  ? formData.lesson_pdf_path
                  : [formData.lesson_pdf_path]
              ).map((file: any, index: number) => (
                <div key={index}>
                  {file instanceof File ? (
                    <span className="text-muted mt-2 ms-2 d-inline-block">
                      {file.name} (selected)
                    </span>
                  ) : (
                    <a
                      href={file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-info mt-2 ms-2 d-inline-block"
                    >
                      {getFileName(file)}
                    </a>
                  )}
                </div>
              ))}
          </div>
          {/* Training Day Number */}
          <div className="col-md-6 mb-3">
            <label>
              <strong>
                Training Day Number
                <sup className="text-danger">*</sup>
              </strong>
            </label>

            <input
              type="text"
              name="plan_number"
              value={formData.plan_number || ""}
              onChange={handleChange}
              className="form-control mt-1"
              placeholder="Enter training day number"
            />

            {errors?.plan_number && (
              <p className="p-0 ps-1 pt-1 text-danger">{errors.plan_number}</p>
            )}
          </div>

          {/* Training Video Resource */}
          <div className="col-md-6 mb-3">
            <label>
              <strong>Training Video Resource</strong>
            </label>

            {/* FIX: type was "input" (not a valid HTML input type, so the
                browser silently falls back to a text box) — that's why a
                file could never actually be picked here and the field
                always looked "empty" on submit. Changed to type="file"
                and added videoRef so it can be cleared after a
                successful submit, same as the PDF field. */}
            <input
              type="input"
              accept="video/*"
              name="lesson_video_path"
              ref={videoRef}
              onChange={handleChange}
              className="form-control mt-1"
            />

            {errors?.lesson_video_path && (
              <p className="p-0 ps-1 pt-1 text-danger">
                {errors.lesson_video_path}
              </p>
            )}

            {formData?.lesson_video_path &&
              (isUrlString(formData.lesson_video_path) ? (
                <a
                  href={formData.lesson_video_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-info mt-2 ms-2 d-inline-block"
                >
                  {getFileName(formData.lesson_video_path)}
                </a>
              ) : (
                <span className="text-muted mt-2 ms-2 d-inline-block">
                  {getFileName(formData.lesson_video_path)} (selected)
                </span>
              ))}
          </div>

          {/* Training ZIP */}
          <div className="col-md-6 mb-3">
            <label>
              <strong>Training ZIP</strong>
            </label>

            <input
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              name="lesson_resource"
              ref={zipRef}
              onChange={handleChange}
              className="form-control mt-1"
            />

            {errors?.lesson_resource && (
              <p className="p-0 ps-1 pt-1 text-danger">
                {errors.lesson_resource}
              </p>
            )}

            {formData?.lesson_resource &&
              (isUrlString(formData.lesson_resource) ? (
                <a
                  href={formData.lesson_resource}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-info mt-2 ms-2 d-inline-block"
                >
                  {getFileName(formData.lesson_resource)}
                </a>
              ) : (
                <span className="text-muted mt-2 ms-2 d-inline-block">
                  {getFileName(formData.lesson_resource)} (selected)
                </span>
              ))}
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary mx-2"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              Saving...
            </>
          ) : (
            <>Save Lesson Plan</>
          )}
        </button>
      </form>
    </div>
  );
};

export default UnitForm;
